import { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { TextField, SubmitButton, EMAIL_RE } from './FormFields';
import { useAuth } from '../../auth/AuthContext';
import { Mail, Key, ArrowLeft } from '../Icons';

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);

  const send = async () => {
    setLoading(true);
    try {
      await forgotPassword(email);
      return true;
    } catch (err) {
      // Don't reveal whether the email is registered; only report connection/server problems.
      if (err.status === 404) return true;
      setError(err.message || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    setError('');
    if (await send()) setSent(true);
  };

  const onResend = async () => {
    setResent(false);
    if (await send()) setResent(true);
  };

  if (sent) {
    return (
      <AuthLayout title="Check your inbox" footer={<Link to="/login" className="auth__back-link"><ArrowLeft width={16} height={16} /> Back to log in</Link>}>
        <div className="sent">
          <div className="sent__icon"><Mail width={28} height={28} /></div>
          <p>
            If an account exists for <strong>{email.trim()}</strong>, we have sent a link to reset your
            password. The link expires in 30 minutes.
          </p>
          <p className="sent__muted">Didn't get it? Check your spam folder, or</p>
          <button type="button" className="btn btn--outline btn--block" onClick={onResend} disabled={loading}>
            {loading && <span className="spinner spinner--dark" aria-hidden="true" />}
            {loading ? 'Sending…' : 'Resend email'}
          </button>
          {resent && <p className="sent__ok" role="status">A new link is on its way.</p>}
          <button type="button" className="link-btn" onClick={() => { setSent(false); setResent(false); }}>
            Use a different email
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="Enter the email you signed up with and we'll send you a reset link."
      footer={<Link to="/login" className="auth__back-link"><ArrowLeft width={16} height={16} /> Back to log in</Link>}
    >
      <div className="auth__badge"><Key width={22} height={22} /></div>
      <form className="auth__form" onSubmit={onSubmit} noValidate>
        <TextField
          id="email"
          label="Email address"
          type="email"
          icon={Mail}
          placeholder="you@example.com"
          autoComplete="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          error={error}
          autoFocus
        />
        <SubmitButton loading={loading}>{loading ? 'Sending link…' : 'Send reset link'}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
